<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import { apiClient } from "@/api/client";
import type { SubscriptionStatus } from "@/api/types";
import SeatPurchase from "@/components/SeatPurchase.vue";
import RlSkeleton from "@/components/ui/RlSkeleton.vue";
import SlideOverPanel from "@/components/ui/SlideOverPanel.vue";
import { UNDER_16_PURCHASE_COPY } from "@/lib/entitlement";

/**
 * "Start your plan" — §4.2's slide-over around `SeatPurchase` (rl-757.47).
 *
 * Self-managing, the same convention as `AddSubjectPanel`: this component
 * owns `?plan=1` end to end, so a host mounts it once with no props and every
 * "Start your plan" affordance is a link that pushes the param. §8 Q3 keeps
 * that param its own rather than branching `?add=1` on a paywall, so the two
 * actions stay distinct in the URL and in analytics — and one convention for
 * opening it means a bookmark, a link out of a recovery notice and the back
 * button all land in the same place. Clearing the param with `replace`, not
 * `push`, keeps the panel out of history: a back-navigation after buying
 * should leave the page, not reopen the form.
 *
 * `SeatPurchase` picks its default holder from the agreement's status (see
 * its `canNameHolder`), so the body waits for `GET /me/subscription` instead
 * of rendering the choice on a guess and swapping it under the reader a tick
 * later. The same call answers §12.1's age question.
 */

const route = useRoute();
const router = useRouter();

const open = computed(() => route.query.plan === "1");

const status = ref<SubscriptionStatus["status"]>("none");
/**
 * §12.1's 16+ answer, tri-state because `null` means NOT YET KNOWN and is
 * treated PERMISSIVELY (§8 Q4, rl-757.45): hiding the only route forward from
 * a 16-year-old because one request was slow is worse than briefly offering a
 * control that answers `age_below_purchase_threshold`, which `SeatPurchase`
 * already has copy for. A confirmed `false` is the one case §7's guard rail
 * makes absolute, and it reaches this panel by deep link only — §4.1 gives a
 * 13-15 account no plan step to press.
 */
const canSelfPurchase = ref<boolean | null>(null);
const loading = ref(false);

watch(
  open,
  (isOpen) => {
    if (!isOpen) return;
    // Refetched on every open rather than cached: `can_self_purchase` flips by
    // itself on the first of the account's birthday month, and the status
    // changes the moment a purchase completes.
    void loadSubscription();
  },
  { immediate: true },
);

async function loadSubscription(): Promise<void> {
  loading.value = true;
  try {
    const sub = await apiClient.getSubscription();
    status.value = sub.status;
    canSelfPurchase.value = sub.can_self_purchase;
  } catch {
    // Unknown on both counts, and both defaults are the permissive ones: no
    // live agreement means the Checkout path, which asks for no address it
    // cannot carry anyway, and an unknown age leaves the CTA for the server's
    // refusal to answer. Nothing is reported, because a purchase surface that
    // explains a failed probe instead of offering the purchase is exactly the
    // dead end §10 forbids.
    status.value = "none";
    canSelfPurchase.value = null;
  } finally {
    loading.value = false;
  }
}

function close(): void {
  const { plan: _plan, ...rest } = route.query;
  void router.replace({ path: route.path, query: rest, hash: route.hash });
}
</script>

<template>
  <SlideOverPanel
    v-if="open"
    title="Start your plan"
    subtitle="£39 a month · every subject and board · cancel any time"
    @close="close"
  >
    <div v-if="loading" class="plan-loading" data-testid="start-plan-loading">
      <RlSkeleton width="100%" :height="36" />
      <RlSkeleton width="100%" :height="120" />
      <RlSkeleton width="60%" :height="12" />
    </div>

    <!-- PRD §12.1 and §7's guard rail: under 16 never sees a self-purchase
         control. An explanation rather than an empty panel, because a deep
         link that opens onto nothing is the dead end §10 forbids — and no
         button beside it, because "Ask someone to pay" is rl-757.27 and has
         no backend (`api/client.ts:83`). The wording is the one every other
         surface uses, so a learner who met it on Account or in the add-subject
         panel does not read a fourth version of the same rule. -->
    <p
      v-else-if="canSelfPurchase === false"
      class="plan-blocked"
      data-testid="start-plan-age-blocked"
    >
      {{ UNDER_16_PURCHASE_COPY }}
    </p>

    <SeatPurchase v-else :subscription-status="status" />
  </SlideOverPanel>
</template>

<style scoped>
.plan-loading {
  display: flex;
  flex-direction: column;
  gap: var(--sp-4);
}

.plan-blocked {
  margin: 0;
  font-size: var(--rl-text-base);
  color: var(--rl-fg-2);
}
</style>
